import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  Bell,
  Crown,
  ChevronDown,
  ChevronLeft,
  X,
  User,
  Settings,
  HelpCircle,
  LogOut,
  ChevronRight,
  Mic,
  MicOff,
  Loader2,
  Sparkles,
  TrendingUp,
  Users,
  DollarSign,
  Calendar,
  BarChart3,
  Send,
  Brain,
  Activity,
  Route,
  Clock,
  Trash2,
  Sun,
  Moon,
  Globe,
  ExternalLink,
  Hash,
  Zap
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import VoiceSearchPopup from './VoiceSearchPopup';

// Get API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.API_BASE_URL || 'https://mars2.mediacle.net';

const Header = ({ 
  sidebarCollapsed = false, 
  setSidebarCollapsed = () => {}, 
  searchQuery = '', 
  setSearchQuery = () => {}, 
  notifications = [], 
  setNotifications = () => {}, 
  showNotifications = false, 
  setShowNotifications = () => {}, 
  showProfileDropdown = false, 
  setShowProfileDropdown = () => {},
  // NEW: Callback ref for profile updates
  onHeaderUpdateRef
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const { theme, toggleTheme, isDark } = useTheme();

  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [domainData, setDomainData] = useState(null);
  const [isDomainLoading, setIsDomainLoading] = useState(false);
  const [aiSearchResults, setAiSearchResults] = useState(null);
  const [isAiSearchLoading, setIsAiSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  const [currentUser, setCurrentUser] = useState({
    name: 'Loading...',
    initials: 'L',
    email: '',
    plan: 'Free',
    tawkEnabled: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [deletingNotifications, setDeletingNotifications] = useState(new Set());
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [tawkLoaded, setTawkLoaded] = useState(false);
  const [showVoicePopup, setShowVoicePopup] = useState(false);

  // Function to check if user has eligible plan AND has enabled tawk
  const shouldShowTawkTo = useCallback((userPlan, tawkEnabled) => {
    const eligiblePlans = ['Growth', 'Pro', 'Enterprise'];
    return eligiblePlans.includes(userPlan) && tawkEnabled === true;
  }, []);

  // Function to load Tawk.to script
  const loadTawkScript = useCallback(() => {
    if (tawkLoaded || typeof window === 'undefined') return;

    // Skip on localhost — Tawk.to CDN blocks requests from non-production origins
    // and causes CORS errors + jsx attribute warnings in the dev console
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return;

    // Check if script already exists
    const existingScript = document.querySelector('script[src*="tawk.to"]');
    if (existingScript) {
      setTawkLoaded(true);
      return;
    }

    // Initialize Tawk_API if not exists
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    // Keep widget minimized (closed) by default
    window.Tawk_API.onLoad = () => {
      if (window.Tawk_API && window.Tawk_API.minimize) {
        window.Tawk_API.minimize();
      }
    };

    // Inject CSS to reduce widget size
    if (!document.getElementById('tawk-custom-style')) {
      const style = document.createElement('style');
      style.id = 'tawk-custom-style';
      style.textContent = `
        .widget-visible iframe[title="chat widget"],
        #tawk-bubble-container {
          transform: scale(0.85) !important;
          transform-origin: bottom right !important;
        }
      `;
      document.head.appendChild(style);
    }

    // Create and inject the script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://embed.tawk.to/67f52af29b5f671906ebc6a3/1ioaqvhsq';
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');

    // Add error handling
    script.onerror = () => {
      console.warn('Failed to load Tawk.to chat widget');
    };

    script.onload = () => {
      setTawkLoaded(true);
    };

    // Insert the script
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    }
  }, [tawkLoaded]);

  // Function to ensure Tawk.to widget exists (but stays minimized)
  const ensureTawkToVisible = useCallback(() => {
    // Don't force-expand — just ensure the widget is present
    if (window.Tawk_API && window.Tawk_API.showWidget) {
      window.Tawk_API.showWidget();
      // Immediately minimize so it doesn't pop open
      if (window.Tawk_API.minimize) {
        window.Tawk_API.minimize();
      }
    }
  }, []);

  // Function to remove Tawk.to script and widget
  const removeTawkScript = useCallback(() => {
    if (!tawkLoaded) return;

    try {
      // Hide the widget if it exists
      if (window.Tawk_API && window.Tawk_API.hideWidget) {
        window.Tawk_API.hideWidget();
      }

      // Remove the widget container if it exists
      const tawkContainer = document.getElementById('tawk-widget-container');
      if (tawkContainer) {
        tawkContainer.remove();
      }

      // Find and remove Tawk.to scripts
      const scripts = document.querySelectorAll('script[src*="tawk.to"]');
      scripts.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });

      // Clean up global variables
      if (window.Tawk_API) {
        delete window.Tawk_API;
      }
      if (window.Tawk_LoadStart) {
        delete window.Tawk_LoadStart;
      }

      setTawkLoaded(false);
    } catch (error) {
      console.warn('Error removing Tawk.to widget:', error);
    }
  }, [tawkLoaded]);

  // Effect to manage Tawk.to based on user plan and tawkEnabled setting
  useEffect(() => {
    if (!isLoading && currentUser.plan) {
      if (shouldShowTawkTo(currentUser.plan, currentUser.tawkEnabled)) {
        if (!tawkLoaded) {
          loadTawkScript();
        } else {
          ensureTawkToVisible();
        }
      } else {
        if (tawkLoaded) {
          removeTawkScript();
        }
      }
    }
  }, [currentUser.plan, currentUser.tawkEnabled, isLoading, shouldShowTawkTo, loadTawkScript, removeTawkScript, tawkLoaded, ensureTawkToVisible]);

  // Ensure Tawk.to is visible on route changes
  useEffect(() => {
    if (tawkLoaded && shouldShowTawkTo(currentUser.plan, currentUser.tawkEnabled)) {
      const timer = setTimeout(() => {
        ensureTawkToVisible();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, tawkLoaded, currentUser.plan, currentUser.tawkEnabled, shouldShowTawkTo, ensureTawkToVisible]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (!shouldShowTawkTo(currentUser.plan, currentUser.tawkEnabled) && tawkLoaded) {
        removeTawkScript();
      }
    };
  }, [currentUser.plan, currentUser.tawkEnabled, shouldShowTawkTo, tawkLoaded, removeTawkScript]);

  // Function to update header data from profile changes
  const updateHeaderData = useCallback((updatedUserData) => {
    setCurrentUser(prev => ({
      ...prev,
      ...updatedUserData
    }));
  }, []);

  // Expose the update function to parent components
  useEffect(() => {
    if (onHeaderUpdateRef && typeof onHeaderUpdateRef === 'object') {
      onHeaderUpdateRef.current = updateHeaderData;
    }
  }, [updateHeaderData, onHeaderUpdateRef]);

  // IMPROVED: API request with better error handling
  const makeApiRequest = useCallback(async (url, options = {}) => {
    try {
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      // Handle non-JSON responses (like redirects)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (response.status === 401) {
          navigate('/login');
          return null;
        }
        throw new Error(`Unexpected response type: ${contentType}`);
      }

      const data = await response.json();

      if (response.status === 401) {
        navigate('/login');
        return null;
      }

      return { data, response };
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }, [navigate]);

  // IMPROVED: Fetch current user data with better error handling
  const fetchCurrentUser = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const result = await makeApiRequest(`${API_BASE_URL}/api/auth/me`);
      if (!result) return; // Redirected to login

      const { data } = result;

      if (data.success && data.data) {
        const userData = {
          id: data.data.id,
          name: data.data.name || `${data.data.firstName || ''} ${data.data.lastName || ''}`.trim(),
          firstName: data.data.firstName,
          lastName: data.data.lastName,
          email: data.data.email,
          plan: data.data.plan || 'Free',
          initials: data.data.initials || (data.data.name ? data.data.name.charAt(0).toUpperCase() : 'U'),
          pricingAccess: data.data.pricingAccess,
          tawkEnabled: data.data.tawkEnabled || false
        };
        setCurrentUser(userData);
        
        // Fetch notifications after getting user data
        fetchNotifications(userData.id);
      }
    } catch (error) {
      console.warn('Failed to fetch user data:', error);
      // Don't redirect on network errors, let user stay on page
    } finally {
      setIsLoading(false);
    }
  }, [makeApiRequest]);

  // IMPROVED: Fetch notifications with better error handling
  const fetchNotifications = useCallback(async (userId) => {
    if (!userId) return;

    try {
      setIsLoadingNotifications(true);

      const result = await makeApiRequest(`${API_BASE_URL}/api/notification`, {
        method: 'POST',
        body: JSON.stringify({ userId })
      });

      if (!result) return; // Redirected to login

      const { data } = result;

      if (data.success && data.data) {
        // Transform API data to match component format
        const transformedNotifications = data.data.map(notification => {
          // Parse notification data if it exists
          let notificationData = null;
          if (notification.notification_data) {
            try {
              notificationData = typeof notification.notification_data === 'string'
                ? JSON.parse(notification.notification_data)
                : notification.notification_data;
            } catch (e) {
              console.error('Error parsing notification_data:', e);
            }
          }

          // Determine notification type based on data
          let type = 'info';
          if (notification.notification_type === 'position_change' && notificationData) {
            type = notificationData.is_improvement ? 'success' : 'warning';
          }

          return {
            id: notification.id,
            title: notification.brand_name || 'New Notification',
            message: notification.message || notification.brand_name || 'You have a new notification',
            time: formatNotificationTime(notification.created_at),
            type: type,
            unread: !notification.read,
            source_brand: notification.source_brand,
            notification_type: notification.notification_type,
            notification_data: notificationData
          };
        });

        setNotifications(transformedNotifications);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      // Fall back to sample notifications on error
      setSampleNotifications();
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [setNotifications, makeApiRequest]);

  // IMPROVED: Mark notification as read with optimistic updates
  const markNotificationAsRead = useCallback(async (notificationId) => {
    // Optimistic update - mark as read immediately in UI
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, unread: false } : notif
      )
    );

    try {
      const result = await makeApiRequest(`${API_BASE_URL}/api/notification/${notificationId}/read`, {
        method: 'PUT',
        body: JSON.stringify({ read: true })
      });

      if (!result) return;

      const { data } = result;

      if (!data.success) {
        console.error('Failed to mark notification as read:', data.error);
        // Revert optimistic update on failure
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId ? { ...notif, unread: true } : notif
          )
        );
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Revert optimistic update on error
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, unread: true } : notif
        )
      );
    }
  }, [setNotifications, makeApiRequest]);

  // NEW: Handle notification click with navigation
  const handleNotificationClick = useCallback(async (notification) => {
    // Mark as read first
    await markNotificationAsRead(notification.id);

    // Close notification panel
    setShowNotifications(false);

    // Navigate based on notification type
    if (notification.notification_type === 'position_change') {
      // Navigate to position tracker page for position change notifications
      navigate('/position-tracker');
    } else if (notification.notification_type === 'agent_draft_ready') {
      // AI drafted a reply that's awaiting approval — jump to the agent page;
      // the Approvals badge will surface it.
      navigate('/affiliate-agent');
    } else if (notification.notification_type === 'inbox_reply') {
      // Reply to outreach landed — open the inbox to read it.
      navigate('/inbox');
    } else {
      // Navigate to notification page for other notifications
      navigate('/notification');
    }
  }, [markNotificationAsRead, setShowNotifications, navigate]);

  // IMPROVED: Mark all notifications as read
  const markAllNotificationsAsRead = useCallback(async () => {
    // Optimistic update
    const originalNotifications = notifications;
    setNotifications(prev => prev.map(notif => ({ ...notif, unread: false })));

    try {
      const result = await makeApiRequest(`${API_BASE_URL}/api/notification/mark-all-read`, {
        method: 'PUT'
      });

      if (!result) return;

      const { data } = result;

      if (!data.success) {
        console.error('Failed to mark all notifications as read:', data.error);
        // Revert on failure
        setNotifications(originalNotifications);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Revert on error
      setNotifications(originalNotifications);
    }
  }, [setNotifications, makeApiRequest, notifications]);

  // IMPROVED: Delete notification with better UX
  const deleteNotification = useCallback(async (notificationId, event) => {
    if (event) {
      event.stopPropagation();
    }

    // Add to deleting set for loading state
    setDeletingNotifications(prev => new Set(prev).add(notificationId));

    // Store original notifications for potential revert
    const originalNotifications = notifications;

    try {
      // Optimistic update - remove from UI immediately
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));

      const result = await makeApiRequest(`${API_BASE_URL}/api/notification/${notificationId}`, {
        method: 'DELETE'
      });

      if (!result) {
        // Revert if redirected
        setNotifications(originalNotifications);
        return;
      }

      const { data } = result;

      if (!data.success) {
        console.error('Failed to delete notification:', data.error);
        // Revert on failure
        setNotifications(originalNotifications);
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      // Revert on error
      setNotifications(originalNotifications);
    } finally {
      // Remove from deleting set
      setDeletingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  }, [setNotifications, makeApiRequest, notifications]);

  // NEW: Delete all notifications
  const deleteAllNotifications = useCallback(async () => {
    if (!window.confirm('Are you sure you want to delete all notifications? This action cannot be undone.')) {
      return;
    }

    setIsDeletingAll(true);
    const originalNotifications = notifications;

    try {
      // Optimistic update
      setNotifications([]);

      const result = await makeApiRequest(`${API_BASE_URL}/api/notification/delete-all`, {
        method: 'DELETE'
      });

      if (!result) {
        setNotifications(originalNotifications);
        return;
      }

      const { data } = result;

      if (!data.success) {
        console.error('Failed to delete all notifications:', data.error);
        setNotifications(originalNotifications);
      } else {
        // Close notification panel after successful deletion
        setShowNotifications(false);
      }
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      setNotifications(originalNotifications);
    } finally {
      setIsDeletingAll(false);
    }
  }, [setNotifications, makeApiRequest, notifications, setShowNotifications]);

  // Format notification time
  const formatNotificationTime = (createdAt) => {
    try {
      const now = new Date();
      const notificationTime = new Date(createdAt);
      
      // Check if date is valid
      if (isNaN(notificationTime.getTime())) {
        return 'Recently';
      }
      
      const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
      
      // For older notifications, show the actual date
      return notificationTime.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting notification time:', error);
      return 'Recently';
    }
  };

  // Set sample notifications as fallback
  const setSampleNotifications = useCallback(() => {
    const sampleNotifications = [
      {
        id: 1,
        title: 'New affiliate joined',
        message: 'Sarah Chen has joined your program',
        time: '2 min ago',
        type: 'success',
        unread: true
      },
      {
        id: 2,
        title: 'Campaign performance',
        message: 'Your Q4 campaign exceeded targets by 15%',
        time: '1 hour ago',
        type: 'success',
        unread: true
      },
      {
        id: 3,
        title: 'Payment processed',
        message: 'Commission payment of $2,340 has been sent',
        time: '3 hours ago',
        type: 'info',
        unread: false
      }
    ];
    setNotifications(sampleNotifications);
  }, [setNotifications]);

  // IMPROVED: Handle logout with better UX
  // IMPROVED: Handle logout with better UX and direct redirect
const handleLogout = useCallback(async () => {
  try {
    setIsLoggingOut(true);
    
    // Clear local data immediately
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    
    // Try to call logout API, but don't wait for it or depend on it
    fetch(`${API_BASE_URL}/api/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    }).catch(error => {
      // Ignore API errors - we're logging out anyway
    });
    
    // Force immediate redirect without waiting for API response
    window.location.href = '/login';
    
  } catch (error) {
    console.error('Logout error:', error);
    // Force logout even on error
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    window.location.href = '/login';
  } finally {
    setIsLoggingOut(false);
  }
}, []);

  // Fetch user data on component mount
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  // Listen for tawk setting changes from Profile page
  useEffect(() => {
    const handleTawkChange = (e) => {
      const { tawkEnabled } = e.detail;
      setCurrentUser(prev => ({ ...prev, tawkEnabled }));
    };
    window.addEventListener('tawkSettingChanged', handleTawkChange);
    return () => window.removeEventListener('tawkSettingChanged', handleTawkChange);
  }, []);

  // IMPROVED: Refresh notifications every 2 minutes and retry on focus
  useEffect(() => {
    if (currentUser.id) {
      const interval = setInterval(() => {
        fetchNotifications(currentUser.id);
      }, 2 * 60 * 1000); // 2 minutes

      // Refresh notifications when window gains focus
      const handleFocus = () => {
        fetchNotifications(currentUser.id);
      };

      window.addEventListener('focus', handleFocus);

      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [currentUser.id, fetchNotifications]);

  const displayNotifications = notifications.length > 0 ? notifications : [];

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
        setSearchFocused(true);
        setShowAiPanel(true);
      }

      if (event.key === 'Escape') {
        if (showAiPanel) {
          setShowAiPanel(false);
          setAiResponse(null);
          setDomainData(null);
          setAiSearchResults(null);
        }
        if (searchFocused) {
          searchInputRef.current?.blur();
          setSearchFocused(false);
          setSearchQuery('');
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showAiPanel, searchFocused, setSearchQuery]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowAiPanel(false);
        setSearchFocused(false);
        setDomainData(null);
        setAiSearchResults(null);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowNotifications, setShowProfileDropdown]);

  // Voice recognition - open voice search popup
  const startVoiceRecognition = useCallback(() => {
    setShowVoicePopup(true);
  }, []);

  // Check if input looks like a domain
  const isDomainLike = useCallback((text) => {
    if (!text || text.length < 3) return false;
    // Check for domain-like patterns (contains at least one dot and no spaces)
    const cleanText = text.trim().toLowerCase();
    const domainPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?\.[a-z]{2,}(\.[a-z]{2,})?$/i;
    const urlPattern = /^(https?:\/\/)?(www\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?\.[a-z]{2,}/i;
    return domainPattern.test(cleanText) || urlPattern.test(cleanText);
  }, []);

  // Domain lookup function
  const performDomainLookup = useCallback(async (domain) => {
    if (!domain || domain.trim().length < 3) return;

    setIsDomainLoading(true);
    setDomainData(null);
    setAiSearchResults(null);
    setShowAiPanel(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/affiliates/domain-lookup`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domain: domain.trim() })
      });

      const data = await response.json();

      if (data.success && data.data) {
        setDomainData(data.data);
        setAiResponse(null);
      } else {
        setDomainData({ notFound: true, domain: domain.trim() });
      }
    } catch (error) {
      console.error('Domain lookup error:', error);
      setDomainData({ error: true, message: 'Failed to look up domain' });
    } finally {
      setIsDomainLoading(false);
    }
  }, []);

  // AI text search function
  const performAiSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 3) return;

    setIsAiSearchLoading(true);
    setAiSearchResults(null);
    setDomainData(null);
    setShowAiPanel(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/affiliates/text-search`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: query.trim() })
      });

      const data = await response.json();

      if (data.success) {
        setAiSearchResults({
          results: data.data || [],
          parsedQuery: data.parsedQuery,
          totalResults: data.totalResults
        });
        setAiResponse(null);
      } else {
        setAiSearchResults({ error: true, message: data.error || 'Search failed' });
      }
    } catch (error) {
      console.error('AI search error:', error);
      setAiSearchResults({ error: true, message: 'Failed to perform search' });
    } finally {
      setIsAiSearchLoading(false);
    }
  }, []);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear previous debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (!value.trim()) {
      setShowAiPanel(false);
      setDomainData(null);
      setAiSearchResults(null);
      return;
    }

    setShowAiPanel(true);

    // Debounce the search
    searchDebounceRef.current = setTimeout(() => {
      if (isDomainLike(value)) {
        // It's a domain - perform domain lookup
        performDomainLookup(value);
      } else if (value.trim().length >= 5) {
        // It's a search query - perform AI search
        performAiSearch(value);
      }
    }, 500); // 500ms debounce
  };

  const handleSearchSubmit = useCallback(() => {
    if (searchQuery.trim()) {
      if (isDomainLike(searchQuery)) {
        performDomainLookup(searchQuery);
      } else {
        performAiSearch(searchQuery);
      }
    }
  }, [searchQuery, isDomainLike, performDomainLookup, performAiSearch]);

  // Profile navigation handler
  const handleProfileNavigation = useCallback((path) => {
    setShowProfileDropdown(false);
    if (path.startsWith('mailto:')) {
      window.location.href = path;
    } else {
      navigate(path);
    }
  }, [navigate, setShowProfileDropdown]);

  // Quick suggestions for AI search
  const quickSuggestions = [
    { icon: TrendingUp, text: 'High traffic affiliates', action: 'search' },
    { icon: Globe, text: 'UK affiliates', action: 'search' },
    { icon: Users, text: 'Casino affiliates', action: 'search' },
    { icon: BarChart3, text: 'Top organic keywords', action: 'search' }
  ];

  const unreadCount = displayNotifications.filter(n => n.unread).length;

  return (
    <header className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-3 md:px-4 py-2 relative">
      <div className="flex items-center justify-between h-10">

        {/* Left Side - Hamburger on mobile, collapse arrow on desktop */}
        <div className="flex items-center">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-md transition-colors duration-200"
          >
            {/* Hamburger icon on mobile */}
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 md:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            {/* Collapse arrow on desktop */}
            <ChevronLeft className={`w-4 h-4 text-blue-600 dark:text-blue-400 transition-transform duration-300 hidden md:block ${sidebarCollapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Center - AI Search (hidden on small mobile, visible from sm+) */}
        <div className="flex-1 max-w-2xl mx-2 md:mx-6 relative hidden sm:block" ref={searchContainerRef}>
        <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${currentUser?.plan?.toLowerCase() === 'free' ? 'text-gray-400' : 'text-blue-600'}`} />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={currentUser?.plan?.toLowerCase() === 'free' ? undefined : handleSearchInput}
              disabled={currentUser?.plan?.toLowerCase() === 'free'}
              onFocus={() => {
                if (currentUser?.plan?.toLowerCase() === 'free') return;
                setSearchFocused(true);
                if (searchQuery.trim()) {
                  setShowAiPanel(true);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  handleSearchSubmit();
                }
              }}
              placeholder={currentUser?.plan?.toLowerCase() === 'free' ? 'Search available on paid plans' : "Search domain or type 'high traffic UK affiliates'..."}
              className={`w-full pl-10 pr-20 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900 focus:border-blue-300 dark:focus:border-blue-600 transition-all duration-300 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 ${currentUser?.plan?.toLowerCase() === 'free' ? 'opacity-60 cursor-not-allowed' : ''}`}
            />
            
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
              {/* Send */}
          {searchQuery.trim() && (
                <button
                  onClick={handleSearchSubmit}
                  className="p-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Send className="w-3 h-3" />
                </button>
              )}
              
              {/* AI Badge */}
             <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-600 text-white rounded-md text-xs font-medium">
                <Brain className="w-3 h-3" />
                <span>AI</span>
              </div>
              
              <kbd className="px-1.5 py-0.5 text-xs text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded">⌘K</kbd>
            </div>
          </div> 

          {/* AI Panel - Domain Data & Search Results */}
          {showAiPanel && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 z-50">
              {/* Header */}
              <div className="p-4 border-b border-gray-100 dark:border-slate-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-slate-800 dark:to-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                      {isDomainLoading || isAiSearchLoading ? (
                        <Loader2 className="w-3 h-3 text-white animate-spin" />
                      ) : domainData ? (
                        <Globe className="w-3 h-3 text-white" />
                      ) : (
                        <Brain className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-800 dark:text-slate-100">
                        {domainData ? 'Domain Insights' : 'AI Search'}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        {isDomainLoading ? 'Looking up domain...' :
                         isAiSearchLoading ? 'Searching...' :
                         domainData ? 'Domain Data' : 'Type to search affiliates'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowAiPanel(false);
                      setAiResponse(null);
                      setDomainData(null);
                      setAiSearchResults(null);
                    }}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                  </button>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {/* Loading State */}
                {(isDomainLoading || isAiSearchLoading) && (
                  <div className="p-6 text-center">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
                    <p className="text-sm text-gray-600 dark:text-slate-400">
                      {isDomainLoading ? 'Looking up domain data...' : 'Searching affiliates...'}
                    </p>
                  </div>
                )}

                {/* Domain Data Display */}
                {!isDomainLoading && domainData && !domainData.notFound && !domainData.error && (
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Globe className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-gray-800 dark:text-slate-100">{domainData.domain}</span>
                      {domainData.rank && (
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                          Rank #{domainData.rank.toLocaleString()}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {/* Organic Traffic */}
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-green-600" />
                          <span className="text-xs text-green-700 dark:text-green-400 font-medium">Organic Traffic</span>
                        </div>
                        <p className="text-lg font-bold text-green-800 dark:text-green-300">
                          {(domainData.organic_traffic || 0).toLocaleString()}
                        </p>
                      </div>

                      {/* Paid Traffic */}
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="w-4 h-4 text-purple-600" />
                          <span className="text-xs text-purple-700 dark:text-purple-400 font-medium">Paid Traffic</span>
                        </div>
                        <p className="text-lg font-bold text-purple-800 dark:text-purple-300">
                          {(domainData.adwords_traffic || 0).toLocaleString()}
                        </p>
                      </div>

                      {/* Organic Keywords */}
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Hash className="w-4 h-4 text-blue-600" />
                          <span className="text-xs text-blue-700 dark:text-blue-400 font-medium">Organic Keywords</span>
                        </div>
                        <p className="text-lg font-bold text-blue-800 dark:text-blue-300">
                          {(domainData.organic_keywords || 0).toLocaleString()}
                        </p>
                      </div>

                      {/* Total Traffic */}
                      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Zap className="w-4 h-4 text-orange-600" />
                          <span className="text-xs text-orange-700 dark:text-orange-400 font-medium">Total Traffic</span>
                        </div>
                        <p className="text-lg font-bold text-orange-800 dark:text-orange-300">
                          {(domainData.total_traffic || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {/* Additional Info */}
                    {(domainData.organic_cost > 0 || domainData.adwords_cost > 0) && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-600">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-slate-400">Organic Cost:</span>
                          <span className="font-medium text-gray-800 dark:text-slate-200">${(domainData.organic_cost || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span className="text-gray-600 dark:text-slate-400">Ads Cost:</span>
                          <span className="font-medium text-gray-800 dark:text-slate-200">${(domainData.adwords_cost || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Domain Not Found */}
                {!isDomainLoading && domainData && domainData.notFound && (
                  <div className="p-6 text-center">
                    <Globe className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 dark:text-slate-400">No data found for</p>
                    <p className="font-semibold text-gray-800 dark:text-slate-200">{domainData.domain}</p>
                  </div>
                )}

                {/* Domain Error */}
                {!isDomainLoading && domainData && domainData.error && (
                  <div className="p-6 text-center">
                    <X className="w-12 h-12 text-red-300 mx-auto mb-3" />
                    <p className="text-sm text-red-600">{domainData.message}</p>
                  </div>
                )}

                {/* AI Search Results */}
                {!isAiSearchLoading && aiSearchResults && !aiSearchResults.error && aiSearchResults.results && (
                  <div className="p-4">
                    {/* Parsed Query Info */}
                    {aiSearchResults.parsedQuery && (
                      <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          <Brain className="w-3 h-3 inline mr-1" />
                          Found {aiSearchResults.totalResults} affiliates
                          {aiSearchResults.parsedQuery.filters.country && ` in ${aiSearchResults.parsedQuery.filters.country}`}
                          {aiSearchResults.parsedQuery.filters.highTraffic && ' with high traffic'}
                          {aiSearchResults.parsedQuery.filters.lowTraffic && ' with low traffic'}
                          {aiSearchResults.parsedQuery.sortBy && ` sorted by ${aiSearchResults.parsedQuery.sortBy}`}
                        </p>
                      </div>
                    )}

                    {/* Results List */}
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                      {aiSearchResults.results.slice(0, 10).map((affiliate, index) => {
                        const domain = affiliate.affiliate_url || affiliate.url || '';
                        const fullUrl = domain.startsWith('http') ? domain : `https://${domain}`;
                        return (
                        <div
                          key={affiliate.id || index}
                          className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg hover:bg-blue-50 dark:hover:bg-slate-600 transition-colors border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
                        >
                          {/* Top row: domain + visit button */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <img
                                src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`}
                                alt=""
                                className="w-4 h-4 rounded flex-shrink-0"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <span className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                                {domain}
                              </span>
                            </div>
                            <a
                              href={fullUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors flex-shrink-0"
                            >
                              Visit
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>

                          {/* Stats row */}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-slate-400">
                            <span className="flex items-center gap-1" title="Total Traffic">
                              <TrendingUp className="w-3 h-3 text-blue-500" />
                              <span className="font-medium text-gray-700 dark:text-slate-300">{(affiliate.totalTraffic || affiliate.total_traffic || 0).toLocaleString()}</span> traffic
                            </span>
                            <span className="flex items-center gap-1" title="Organic Traffic">
                              <Search className="w-3 h-3 text-green-500" />
                              <span className="font-medium text-gray-700 dark:text-slate-300">{(affiliate.organicTraffic || affiliate.organic_traffic || 0).toLocaleString()}</span> organic
                            </span>
                            <span className="flex items-center gap-1" title="Keywords">
                              <Hash className="w-3 h-3 text-purple-500" />
                              <span className="font-medium text-gray-700 dark:text-slate-300">{(affiliate.organicKeywords || affiliate.organic_keywords || 0).toLocaleString()}</span> kw
                            </span>
                            {affiliate.rank > 0 && (
                              <span className="flex items-center gap-1" title="Rank">
                                <TrendingUp className="w-3 h-3 text-orange-500" />
                                Rank <span className="font-medium text-gray-700 dark:text-slate-300">{affiliate.rank.toLocaleString()}</span>
                              </span>
                            )}
                          </div>

                          {/* Verticals + Geos */}
                          {((affiliate.verticals && affiliate.verticals.length > 0) || (affiliate.topGeos && affiliate.topGeos.length > 0)) && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {affiliate.verticals?.map((v, i) => (
                                <span key={`v-${i}`} className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                  {v}
                                </span>
                              ))}
                              {affiliate.topGeos?.map((geo, i) => (
                                <span key={`g-${i}`} className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-300 rounded">
                                  {geo}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>

                    {aiSearchResults.results.length === 0 && (
                      <div className="text-center py-4">
                        <Search className="w-8 h-8 text-gray-300 dark:text-slate-600 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 dark:text-slate-400">No affiliates found</p>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Search Error */}
                {!isAiSearchLoading && aiSearchResults && aiSearchResults.error && (
                  <div className="p-6 text-center">
                    <X className="w-12 h-12 text-red-300 mx-auto mb-3" />
                    <p className="text-sm text-red-600">{aiSearchResults.message}</p>
                  </div>
                )}

                {/* Default State - Quick suggestions */}
                {!isDomainLoading && !isAiSearchLoading && !domainData && !aiSearchResults && (
                  <div className="p-4">
                    <div className="text-sm text-gray-600 dark:text-slate-400 mb-3 text-center">
                      <Brain className="w-4 h-4 inline mr-2" />
                      Try typing a domain or natural language query
                    </div>
                    <div className="space-y-2 text-xs text-gray-500 dark:text-slate-500">
                      <p><span className="font-medium">Domain lookup:</span> example.com</p>
                      <p><span className="font-medium">AI search:</span> "high traffic UK affiliates"</p>
                      <p><span className="font-medium">AI search:</span> "casino affiliates in Germany"</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      {quickSuggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSearchQuery(suggestion.text);
                            performAiSearch(suggestion.text);
                          }}
                          className="flex items-center gap-2 p-3 text-left hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg transition-colors border border-gray-100 dark:border-slate-600"
                        >
                          <suggestion.icon className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-gray-700 dark:text-slate-300">{suggestion.text}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-3 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
                <div className="flex items-center justify-center text-xs text-gray-500 dark:text-slate-400">
                  <span>Press ⌘K to search • Paste a domain or type a query</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-1.5 md:gap-3">

          {/* Mobile Search Button - only on small screens */}
          <button
            onClick={() => {
              const searchEl = searchContainerRef?.current;
              if (searchEl) {
                searchEl.classList.toggle('hidden');
                searchEl.classList.toggle('block');
                if (!searchEl.classList.contains('hidden')) {
                  searchInputRef?.current?.focus();
                }
              }
            }}
            className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-all sm:hidden"
          >
            <Search className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </button>

          {/* Theme Toggle Button */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200 group"
            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDark ? (
              <Sun className="w-4 h-4 text-yellow-500 group-hover:text-yellow-400 transition-colors" />
            ) : (
              <Moon className="w-4 h-4 text-slate-600 group-hover:text-slate-800 transition-colors" />
            )}
          </button>

          {/* Plan Badge */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-600 text-white rounded-lg text-xs font-medium">
            <Crown className="w-3 h-3" />
            <span>{currentUser.plan}</span>
          </div>

          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-1.5 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200"
            >
              <Bell className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              {isLoadingNotifications && (
                <Loader2 className="absolute -top-0.5 -right-0.5 w-3 h-3 animate-spin text-blue-600 dark:text-blue-400" />
              )}
              {!isLoadingNotifications && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1.5 min-w-[18px] h-[18px] px-1 bg-blue-600 text-white text-[10px] rounded-full flex items-center justify-center font-medium leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-sm bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 z-50">
                <div className="p-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div>
                        <h3 className="text-sm font-medium text-gray-800 dark:text-slate-100">Notifications</h3>
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{unreadCount} unread</p>
                      </div>
                      {isLoadingNotifications && (
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                      )}
                    </div>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllNotificationsAsRead}
                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="max-h-80 overflow-y-auto">
                  {displayNotifications.length === 0 ? (
                    <div className="p-8 text-center">
                      <Bell className="w-8 h-8 text-gray-300 dark:text-slate-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-slate-400">No notifications yet</p>
                    </div>
                  ) : (
                    displayNotifications.map((notification) => {
                      // Check if this is a position change notification
                      const isPositionChange = notification.notification_type === 'position_change' && notification.notification_data;
                      const posData = notification.notification_data;

                      return (
                        <div
                          key={notification.id}
                          className={`group p-4 border-b border-gray-50 dark:border-slate-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors relative ${
                            notification.unread
                              ? isPositionChange && posData?.is_improvement
                                ? 'bg-green-50/30 dark:bg-green-900/20 border-l-2 border-l-green-600'
                                : isPositionChange
                                ? 'bg-orange-50/30 dark:bg-orange-900/20 border-l-2 border-l-orange-600'
                                : 'bg-blue-50/30 dark:bg-blue-900/20 border-l-2 border-l-blue-600'
                              : ''
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start gap-3">
                            {/* Status indicator with icon for position changes */}
                            {isPositionChange ? (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center mt-1 ${
                                posData.is_improvement
                                  ? 'bg-green-100 dark:bg-green-900/40'
                                  : 'bg-orange-100 dark:bg-orange-900/40'
                              }`}>
                                <TrendingUp className={`w-4 h-4 ${
                                  posData.is_improvement
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-orange-600 dark:text-orange-400 rotate-180'
                                }`} />
                              </div>
                            ) : (
                              <div className={`w-2 h-2 rounded-full mt-2 ${
                                notification.unread ? 'bg-blue-600 dark:bg-blue-400' : 'bg-gray-300 dark:bg-slate-600'
                              }`} />
                            )}

                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-800 dark:text-slate-100 text-sm flex items-center gap-2">
                                {notification.title}
                                {isPositionChange && (
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    posData.is_improvement
                                      ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400'
                                      : 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400'
                                  }`}>
                                    {posData.is_improvement ? '↑ Improved' : '↓ Dropped'}
                                  </span>
                                )}
                              </h4>

                              {/* Position change details */}
                              {isPositionChange && posData ? (
                                <div className="mt-2 space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500 dark:text-slate-400 font-medium">Position:</span>
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-mono text-base font-semibold text-gray-700 dark:text-slate-200">
                                        #{posData.old_position}
                                      </span>
                                      <span className="text-gray-400 dark:text-slate-500">→</span>
                                      <span className={`font-mono text-base font-semibold ${
                                        posData.is_improvement ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'
                                      }`}>
                                        #{posData.new_position}
                                      </span>
                                    </div>
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                      posData.is_improvement
                                        ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400'
                                        : 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400'
                                    }`}>
                                      {posData.position_change > 0 ? '+' : ''}{posData.position_change}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1">
                                    <span className="inline-block w-1 h-1 rounded-full bg-gray-400 dark:bg-slate-500"></span>
                                    {posData.country}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-600 dark:text-slate-300 mt-1 line-clamp-2">{notification.message}</p>
                              )}

                              <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">{notification.time}</p>
                            </div>

                            {/* Delete Button */}
                            <button
                              onClick={(e) => deleteNotification(notification.id, e)}
                              disabled={deletingNotifications.has(notification.id)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-full text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Delete notification"
                            >
                              {deletingNotifications.has(notification.id) ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Footer with Clear All button */}
                {displayNotifications.length > 0 && (
                  <div className="p-3 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 rounded-b-xl">
                    <button
                      onClick={deleteAllNotifications}
                      disabled={isDeletingAll}
                      className="w-full text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                    >
                      {isDeletingAll ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-3 h-3" />
                          <span>Clear all notifications</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Profile */}
          <div className="relative" ref={profileRef}>
            <div
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg p-1 transition-colors duration-200"
            >
              {/* FIXED: Avatar with better circular design and responsive sizing */}
              <div className="w-6 h-6 min-w-6 min-h-6 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-xs overflow-hidden">
                {isLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <span className="select-none leading-none">{currentUser.initials}</span>
                )}
              </div>
              <div className="hidden md:block">
                <div className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate max-w-[120px]">
                  {isLoading ? 'Loading...' : currentUser.name}
                </div>
                <div className="text-gray-500 dark:text-slate-400 text-xs">{currentUser.plan}</div>
              </div>
              <ChevronDown className={`w-3 h-3 text-blue-600 transition-transform duration-200 ${showProfileDropdown ? 'rotate-180' : ''}`} />
            </div>

            {showProfileDropdown && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 z-50">
                <div className="p-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
                  <div className="flex items-center gap-3">
                    {/* FIXED: Larger avatar in dropdown with consistent circular design */}
                    <div className="w-10 h-10 min-w-10 min-h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm overflow-hidden">
                      <span className="select-none leading-none">{currentUser.initials}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h6 className="text-sm font-medium text-gray-800 dark:text-slate-100 truncate">{currentUser.name}</h6>
                      <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{currentUser.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs font-medium">
                          Online
                        </div>
                        <div className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                          {currentUser.plan}
                        </div>
                     
                      </div>
                    </div>
                  </div>
                </div>

             <div className="py-1">
  {[
    { icon: User, label: 'My Profile', path: '/profile' },
    { icon: Crown, label: 'Upgrade Plan', path: '/pricing-plan' },
    { icon: HelpCircle, label: 'Help & Support', path: 'mailto:mars@mediacle.com' }
  ]
    .filter(item => !(item.label === 'Upgrade Plan' && currentUser.plan === 'Enterprise'))
    .map((item, index) => (
      <button
        key={index}
        onClick={() => handleProfileNavigation(item.path)}
        className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors text-left"
      >
        <item.icon className="w-4 h-4 text-blue-600" />
        <span className="text-sm">{item.label}</span>
        {/* Show live chat indicator for Help & Support if user has access */}
        {item.label === 'Help & Support' && shouldShowTawkTo(currentUser.plan, currentUser.tawkEnabled) && (
          <span className="ml-auto text-xs text-green-600 font-medium">Live Chat</span>
        )}
      </button>
    ))}
</div>


                <div className="p-3 border-t border-gray-100 dark:border-slate-700">
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg transition-colors hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoggingOut ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Signing Out...</span>
                      </>
                    ) : (
                      <>
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Voice Search Popup */}
      <VoiceSearchPopup
        isOpen={showVoicePopup}
        onClose={() => setShowVoicePopup(false)}
      />
    </header>
  );
};

export default Header;